/**
 * MenuItemDetailModal
 * ───────────────────
 * Full-screen modal that opens when a customer taps any menu item card or
 * list row. Shows the dish in detail: hero photo, story, ingredients,
 * dietary, calories + macros, allergens, and — if the kitchen has paired
 * wines for this dish — a "Pair with this wine" block that lets the diner
 * add a paired wine by glass or by bottle in one tap, without leaving the
 * dialog.
 *
 * Designed to work for ANY restaurant tenant (Caffè Roma,
 * etc.) — the layout pulls only standard fields and is data-driven by
 * `wine_pairing_ids`.
 */
import { useEffect, useMemo, useState } from "react";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { ArrowUp, ChevronDown, Plus, Sparkles, Wine, X } from "lucide-react";
import { api, money } from "@/lib/api";
import { useCart } from "@/context/CartContext";
import { DietaryBadges } from "@/components/DietaryBadges";
import { toast } from "sonner";

const ALLERGEN_MAP = {
  dairy: { label: "Contains dairy", emoji: "🥛" },
  gluten: { label: "Contains gluten", emoji: "🌾" },
  nuts: { label: "Contains tree nuts", emoji: "🌰" },
  peanuts: { label: "Contains peanuts", emoji: "🥜" },
  egg: { label: "Contains egg", emoji: "🥚" },
  soy: { label: "Contains soy", emoji: "🫘" },
  sesame: { label: "Contains sesame", emoji: "🟤" },
  shellfish: { label: "Contains shellfish", emoji: "🦐" },
  fish: { label: "Contains fish", emoji: "🐟" },
};

function detectAllergens(item) {
  const text = `${item.name || ""} ${item.description || ""} ${item.long_description || ""}`.toLowerCase();
  const found = new Set();
  if (/\b(milk|cheese|cream|butter|yoghurt|mascarpone|burrata|ricotta|gelato|parmigiano|grana|taleggio)\b/.test(text)) found.add("dairy");
  if (/\b(wheat|bread|pasta|pizza|gnocchi|brioche|sourdough|flour|crumb|toast|biscotti|cornetto|pancake|focaccia)\b/.test(text)) found.add("gluten");
  if (/\b(walnut|almond|hazelnut|pistachio|pine\s?nut|cashew)\b/.test(text)) found.add("nuts");
  if (/\b(egg|frittata|carbonara|tiramis[uù]|pancake|brioche)\b/.test(text)) found.add("egg");
  if (/\b(salmon|tuna|anchov|sardine|prosecco)\b/.test(text)) found.add("fish");
  if (/\b(calamari|prawn|shrimp|crab|lobster|mussel|oyster)\b/.test(text)) found.add("shellfish");
  if (/\b(sesame|tahini)\b/.test(text)) found.add("sesame");
  if (/\bsoy\b/.test(text)) found.add("soy");
  return Array.from(found);
}

function ingredientsFromText(item) {
  // Prefer explicit ingredient fields, then heuristics on description.
  if (Array.isArray(item.ingredients_display) && item.ingredients_display.length) {
    return item.ingredients_display;
  }
  if (Array.isArray(item.ingredients) && item.ingredients.length) {
    return item.ingredients;
  }
  // Heuristic: split long_description / description on commas in the first sentence.
  const text = item.long_description || item.description || "";
  const first = text.split(/[.!?]/)[0] || "";
  const parts = first.split(/,| and | with | served | finished /i).map((p) => p.trim()).filter(Boolean);
  return parts.length >= 3 ? parts.slice(0, 12) : [];
}

export default function MenuItemDetailModal({ item, onClose, onAddPrimary, resolvePairings, onAddWine, memberAllergens = [] }) {
  const [pairings, setPairings] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pickedAddons, setPickedAddons] = useState(() => new Set());
  // iter376 — addon_groups: {groupIdx: Set(optionIdx)}. Enforces "single"
  // radio semantics vs "multi" checkbox semantics.
  const [groupPicks, setGroupPicks] = useState({});
  const cart = useCart();

  // iter377 — scroll affordances: a bouncing down-chevron while more content
  // sits below the fold, and a back-to-top button once scrolled. A callback
  // ref + ResizeObserver make this robust to the modal's mount/animation
  // timing and to async content (pairings, images) changing scroll height.
  const [scrollEl, setScrollEl] = useState(null);
  const [showScrollDown, setShowScrollDown] = useState(false);
  const [showBackToTop, setShowBackToTop] = useState(false);
  useEffect(() => {
    if (!scrollEl) return undefined;
    const update = () => {
      const { scrollTop, scrollHeight, clientHeight } = scrollEl;
      setShowScrollDown(scrollHeight - scrollTop - clientHeight > 24);
      setShowBackToTop(scrollTop > 160);
    };
    update();
    scrollEl.addEventListener("scroll", update, { passive: true });
    const ro = new ResizeObserver(update);
    ro.observe(scrollEl);
    return () => {
      scrollEl.removeEventListener("scroll", update);
      ro.disconnect();
    };
  }, [scrollEl, item?.id, item?.name]);
  const addonGroups = Array.isArray(item?.addon_groups) ? item.addon_groups : [];

  // Reset addon selection whenever a new item is opened. For required
  // single-select groups, auto-pick the first option so the CTA works.
  useEffect(() => {
    setPickedAddons(new Set());
    const seed = {};
    addonGroups.forEach((g, gi) => {
      if (g?.type === "single" && Array.isArray(g.options) && g.options.length && g.required) {
        seed[gi] = new Set([0]);
      } else {
        seed[gi] = new Set();
      }
    });
    setGroupPicks(seed);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [item?.id, item?.name]);

  // Auto-calc: base + sum of ticked flat addons + sum of ticked group options
  const addonTotal = useMemo(() => {
    let t = 0;
    if (item && Array.isArray(item.addons)) {
      pickedAddons.forEach((idx) => { t += Number(item.addons[idx]?.price) || 0; });
    }
    Object.entries(groupPicks).forEach(([gi, set]) => {
      const g = addonGroups[Number(gi)];
      if (!g || !Array.isArray(g.options)) return;
      set.forEach((oi) => { t += Number(g.options[oi]?.price) || 0; });
    });
    return t;
  }, [item, pickedAddons, groupPicks, addonGroups]);
  const finalPrice = (Number(item?.price) || 0) + addonTotal;

  function toggleAddon(idx) {
    setPickedAddons((prev) => {
      const next = new Set(prev);
      if (next.has(idx)) next.delete(idx); else next.add(idx);
      return next;
    });
  }

  function toggleGroupOption(gi, oi, groupType) {
    setGroupPicks((prev) => {
      const cur = new Set(prev[gi] || []);
      if (groupType === "single") {
        // Radio semantics — replace the selection.
        if (cur.has(oi)) return prev;  // clicking the same one — keep it
        return { ...prev, [gi]: new Set([oi]) };
      }
      if (cur.has(oi)) cur.delete(oi); else cur.add(oi);
      return { ...prev, [gi]: cur };
    });
  }

  // Flatten the selected group options into a list for the cart payload.
  function collectSelectedGroupOptions() {
    const out = [];
    Object.entries(groupPicks).forEach(([gi, set]) => {
      const g = addonGroups[Number(gi)];
      if (!g) return;
      Array.from(set).forEach((oi) => {
        const opt = g.options?.[oi];
        if (opt) out.push({ group: g.name, name: opt.name, price: Number(opt.price) || 0 });
      });
    });
    return out;
  }

  useEffect(() => {
    if (!item || !Array.isArray(item.wine_pairing_ids) || item.wine_pairing_ids.length === 0) {
      setPairings([]);
      return;
    }
    setLoading(true);
    (async () => {
      try {
        // Host context can supply its own pairing resolver (e.g. the venue
        // pre-order menu resolves against its already-loaded item list).
        const results = resolvePairings
          ? await resolvePairings(item.wine_pairing_ids)
          : await Promise.all(
              item.wine_pairing_ids.map((id) =>
                api.get(`/menu/${id}`).then((r) => r.data).catch(() => null),
              ),
            );
        setPairings((results || []).filter(Boolean));
      } finally {
        setLoading(false);
      }
    })();
  }, [item, resolvePairings]);

  if (!item) return null;

  // Prefer server-side (AI-enriched) FSANZ allergens; regex heuristics as fallback.
  const fsanzAllergens = Array.isArray(item.allergens) ? item.allergens : [];
  const allergens = fsanzAllergens.length ? [] : detectAllergens(item);
  const myHits = fsanzAllergens.filter((a) => memberAllergens.includes(a));
  const ingredients = ingredientsFromText(item);
  const hasMacros = item.calories || item.protein_g || item.carbs_g || item.fat_g;

  function addWine(wine, serving) {
    if (onAddWine) {
      onAddWine(wine, serving);
      toast.success(`${wine.name} (${serving}) added`);
      return;
    }
    const bottlePrice = (wine.addons || [])
      .find((ad) => (ad.name || "").toLowerCase() === "serving")
      ?.options?.find((o) => (o.name || "").toLowerCase().includes("bottle"))?.price || 0;
    const base = Number(wine.price) || 0;
    const finalPrice = serving === "bottle" ? base + Number(bottlePrice) : base;
    cart.addLine({
      item_id: wine.id,
      name: wine.name,
      base_price: finalPrice,
      quantity: 1,
      selected_addons: [{ name: serving === "bottle" ? "Bottle (750ml)" : "Glass (150ml)", price: 0 }],
      notes: `Paired with ${item.name}`,
      calories: Number(wine.calories) || 0,
      tags: wine.tags || [],
      price_column: "",
      price_label: serving === "bottle" ? "Bottle" : "Glass",
    });
    toast.success(`${wine.name} (${serving}) added to cart`);
  }

  return (
    <Dialog open={!!item} onOpenChange={(open) => !open && onClose?.()}>
      <DialogContent
        data-testid="menu-item-detail-modal"
        className="max-w-2xl p-0 overflow-hidden gap-0 max-h-[92vh] sm:max-h-[90vh] flex flex-col rounded-2xl shadow-2xl bg-white text-[#121614]"
      >
        <DialogTitle className="sr-only">{item.name}</DialogTitle>
        {/* Hero image */}
        {item.image_url && (
          <div className="relative w-full h-40 sm:h-52 max-h-[30vh] bg-[#F3F0EA] overflow-hidden shrink-0">
            <img
              src={item.image_url}
              alt={item.name}
              className="w-full h-full object-cover"
              onError={(e) => { e.target.style.display = "none"; }}
            />
            <button
              data-testid="menu-detail-close"
              onClick={() => onClose?.()}
              className="absolute top-3 right-3 w-9 h-9 rounded-full bg-black/40 hover:bg-black/60 backdrop-blur text-white grid place-items-center"
              aria-label="Close"
            >
              <X className="w-4 h-4" strokeWidth={2} />
            </button>
            {(item.tags || []).includes("chef_special") && (
              <Badge className="absolute top-3 left-3 bg-[#C05746] hover:bg-[#C05746] text-white border-0 gap-1">
                <Sparkles className="h-3 w-3" strokeWidth={1.5} /> Chef's Special
              </Badge>
            )}
          </div>
        )}

        {/* Scrollable body — flex-1 min-h-0 lets it own the remaining
            vertical space and actually scroll inside the modal. */}
        <div ref={setScrollEl} className="flex-1 min-h-0 overflow-y-auto px-10 pr-12 py-8 space-y-7">
          <header>
            <h2 className="font-serif-display text-2xl md:text-3xl leading-tight" data-testid="menu-detail-name">
              {item.name}
            </h2>
            {item.origin && (
              <p className="text-[11px] uppercase tracking-[0.18em] text-[#5A7247] font-bold mt-1.5">
                {item.origin}
              </p>
            )}
            <div className="mt-3 flex items-center gap-3 flex-wrap">
              <span className="text-2xl font-medium text-[#121614] tabular-nums" data-testid="menu-detail-price">
                {money(Number(item.price) || 0)}
              </span>
              <DietaryBadges tags={item.tags} testIdPrefix="detail-diet" />
              {(Array.isArray(item.dietary) ? item.dietary : []).map((d) => (
                <span
                  key={d}
                  data-testid={`detail-dietary-${d}`}
                  className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-emerald-50 border border-emerald-200 text-[11px] font-semibold uppercase tracking-wide text-emerald-700"
                >
                  {String(d).replace(/-/g, " ")}
                </span>
              ))}
            </div>
          </header>

          {/* Short (menu-tile) description sits directly under the header for
              quick context. The longer AI story appears after add-on picks. */}
          {item.description && (
            <p className="text-[14px] text-[#5C625E] leading-relaxed -mt-3" data-testid="menu-detail-short-desc">
              {item.description}
            </p>
          )}

          {/* iter376 — Structured add-on groups (Doneness / Sides / Sauces / Toppers).
              MOVED to the top of the body so the required Doneness picker
              is visible above the fold on first paint. Single-select groups
              render as radios, multi-select as checkboxes. */}
          {addonGroups.length > 0 && (
            <section data-testid="menu-detail-addon-groups" className="space-y-4">
              {addonGroups.map((g, gi) => {
                const type    = g?.type === "single" ? "single" : "multi";
                const picks   = groupPicks[gi] || new Set();
                const inputT  = type === "single" ? "radio" : "checkbox";
                const options = Array.isArray(g?.options) ? g.options : [];
                if (!options.length) return null;
                return (
                  <div key={gi} data-testid={`menu-detail-group-${gi}`}>
                    <h3 className="text-[10px] uppercase tracking-[0.2em] font-bold text-[#5A7247] mb-2 flex items-center gap-2">
                      <span>{g.name || `Options ${gi + 1}`}</span>
                      {g.required && (
                        <span className="text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded bg-[#C05746] text-white">
                          Required
                        </span>
                      )}
                      {type === "single" && !g.required && (
                        <span className="text-[9px] font-normal italic text-[#8A8A8A]">pick one</span>
                      )}
                    </h3>
                    <div className="rounded-xl border border-[#E8E5DF] divide-y divide-[#E8E5DF] overflow-hidden">
                      {options.map((opt, oi) => (
                        <label
                          key={oi}
                          className="flex items-center justify-between gap-3 px-3 py-2.5 cursor-pointer hover:bg-[#FBF8F3]"
                          data-testid={`menu-detail-group-${gi}-opt-${oi}`}
                        >
                          <div className="flex items-center gap-3 min-w-0">
                            <input
                              type={inputT}
                              name={type === "single" ? `menu-detail-group-${gi}` : undefined}
                              checked={picks.has(oi)}
                              onChange={() => toggleGroupOption(gi, oi, type)}
                              className="w-4 h-4 accent-[#C05746]"
                            />
                            <span className="text-[13px] text-[#121614] truncate">{opt.name}</span>
                          </div>
                          <span className="text-[12px] tabular-nums text-[#5C625E] shrink-0">
                            {Number(opt.price) > 0 ? `+${money(Number(opt.price))}` : "\u2014"}
                          </span>
                        </label>
                      ))}
                    </div>
                  </div>
                );
              })}
            </section>
          )}

          {/* Flat add-ons list — legacy / simple items. Sits with the groups. */}
          {Array.isArray(item.addons) && item.addons.length > 0 && (
            <section data-testid="menu-detail-addons">
              <h3 className="text-[10px] uppercase tracking-[0.2em] font-bold text-[#5A7247] mb-2">Add-ons</h3>
              <div className="rounded-xl border border-[#E8E5DF] divide-y divide-[#E8E5DF] overflow-hidden">
                {item.addons.map((a, idx) => (
                  <label
                    key={idx}
                    className="flex items-center justify-between gap-3 px-3 py-2.5 cursor-pointer hover:bg-[#FBF8F3]"
                    data-testid={`menu-detail-addon-row-${idx}`}
                  >
                    <div className="flex items-center gap-3 min-w-0">
                      <input
                        type="checkbox"
                        checked={pickedAddons.has(idx)}
                        onChange={() => toggleAddon(idx)}
                        className="w-4 h-4 accent-[#C05746]"
                        data-testid={`menu-detail-addon-${idx}`}
                      />
                      <span className="text-[13px] text-[#121614] truncate">{a.name}</span>
                    </div>
                    <span className="text-[12px] tabular-nums text-[#5C625E] shrink-0">+{money(Number(a.price) || 0)}</span>
                  </label>
                ))}
              </div>
              {pickedAddons.size > 0 && (
                <p className="mt-2 text-[11px] text-[#5C625E]" data-testid="menu-detail-addon-total">
                  Add-ons total: <b className="tabular-nums text-[#121614]">+{money(addonTotal)}</b>
                </p>
              )}
            </section>
          )}

          {/* Long AI story — full description under the addon picker */}
          {item.long_description && item.long_description !== item.description && (
            <section data-testid="menu-detail-story">
              <h3 className="text-[10px] uppercase tracking-[0.2em] font-bold text-[#5A7247] mb-2">The Story</h3>
              <p className="text-[14px] leading-relaxed text-[#3a3a36]">
                {item.long_description}
              </p>
            </section>
          )}

          {/* Ingredients */}
          {ingredients.length > 0 && (
            <section data-testid="menu-detail-ingredients">
              <h3 className="text-[10px] uppercase tracking-[0.2em] font-bold text-[#5A7247] mb-2">
                Made with
              </h3>
              <ul className="flex flex-wrap gap-1.5">
                {ingredients.map((ing, idx) => (
                  <li
                    key={idx}
                    className="px-2.5 py-1 rounded-full bg-[#F3F0EA] text-[12px] text-[#5C625E] capitalize"
                  >
                    {ing}
                  </li>
                ))}
              </ul>
            </section>
          )}

          {/* Personal allergen alert — matched against the member profile */}
          {myHits.length > 0 && (
            <section
              data-testid="menu-detail-my-allergen-alert"
              className="rounded-xl border-2 border-[#B0483A] bg-[#FDEEEA] px-4 py-3"
            >
              <p className="text-[13px] font-bold text-[#8f3527]">
                ⚠ Contains {myHits.join(", ")} — flagged in your allergen profile
              </p>
            </section>
          )}

          {/* Allergens (FSANZ — AI-enriched) */}
          {fsanzAllergens.length > 0 && (
            <section data-testid="menu-detail-allergens">
              <h3 className="text-[10px] uppercase tracking-[0.2em] font-bold text-[#C05746] mb-2">
                Allergen info
              </h3>
              <div className="flex flex-wrap gap-1.5">
                {fsanzAllergens.map((a) => {
                  const hit = memberAllergens.includes(a);
                  return (
                    <span
                      key={a}
                      data-testid={`detail-allergen-${a}`}
                      className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[12px] border ${
                        hit
                          ? "bg-[#B0483A] border-[#B0483A] text-white font-bold"
                          : "bg-[#FFF5F2] border-[#F4D4CC] text-[#8a3a2a]"}`}
                    >
                      {hit ? "⚠ " : ""}{a}
                    </span>
                  );
                })}
              </div>
              <p className="mt-1.5 text-[11px] text-[#9BA19C]">
                Allergen data covers typical hidden ingredients too — always tell
                the kitchen about severe allergies.
              </p>
            </section>
          )}

          {/* Allergens (heuristic fallback) */}
          {allergens.length > 0 && (
            <section data-testid="menu-detail-allergens">
              <h3 className="text-[10px] uppercase tracking-[0.2em] font-bold text-[#C05746] mb-2">
                Allergen info
              </h3>
              <div className="flex flex-wrap gap-1.5">
                {allergens.map((code) => (
                  <span
                    key={code}
                    className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-[#FFF5F2] border border-[#F4D4CC] text-[12px] text-[#8a3a2a]"
                  >
                    <span>{ALLERGEN_MAP[code]?.emoji}</span>
                    {ALLERGEN_MAP[code]?.label || code}
                  </span>
                ))}
              </div>
            </section>
          )}

          {/* Calories + Macros */}
          {hasMacros ? (
            <section data-testid="menu-detail-macros" className="grid grid-cols-4 gap-2 text-center">
              <Macro label="Calories" value={item.calories ? `${Math.round(item.calories)}` : "—"} unit="kcal" />
              <Macro label="Carbs"    value={item.carbs_g != null ? Number(item.carbs_g).toFixed(0) : "—"} unit="g" />
              <Macro label="Protein"  value={item.protein_g != null ? Number(item.protein_g).toFixed(0) : "—"} unit="g" />
              <Macro label="Fat"      value={item.fat_g != null ? Number(item.fat_g).toFixed(0) : "—"} unit="g" />
            </section>
          ) : null}

          {/* Wine pairing */}
          {pairings.length > 0 && (
            <section data-testid="menu-detail-wine-pairings">
              <h3 className="text-[10px] uppercase tracking-[0.2em] font-bold text-[#7c2d12] mb-3 flex items-center gap-1.5">
                <Wine className="w-3 h-3" strokeWidth={2} />
                Sommelier's pairing
              </h3>
              {item.pairing_note && (
                <p className="text-[13px] italic text-[#5C625E] mb-2" data-testid="menu-detail-pairing-note">
                  {item.pairing_note}
                </p>
              )}
              <div className="space-y-2">
                {pairings.map((w) => {
                  const bottlePrice = (w.addons || [])
                    .find((ad) => (ad.name || "").toLowerCase() === "serving")
                    ?.options?.find((o) => (o.name || "").toLowerCase().includes("bottle"))?.price || 0;
                  const glassPrice = Number(w.price) || 0;
                  const bottleTotal = glassPrice + Number(bottlePrice);
                  return (
                    <div
                      key={w.id}
                      data-testid={`pairing-${w.id}`}
                      className="flex items-center justify-between gap-3 p-3 rounded-xl border border-[#E8E5DF] bg-gradient-to-br from-[#FBF8F3] to-white"
                    >
                      <div className="min-w-0 flex-1">
                        <p className="font-semibold text-[14px] text-[#121614] truncate">{w.name}</p>
                        {w.origin && <p className="text-[11px] text-[#5C625E] truncate">{w.origin}</p>}
                      </div>
                      <div className="flex items-center gap-2 shrink-0">
                        <Button
                          data-testid={`pairing-${w.id}-glass`}
                          size="sm"
                          onClick={() => addWine(w, "glass")}
                          className="bg-[#7c2d12] hover:bg-[#5a1e0b] text-white rounded-full h-8 px-3 text-[11px]"
                        >
                          Glass {money(glassPrice)}
                        </Button>
                        {bottlePrice > 0 && (
                          <Button
                            data-testid={`pairing-${w.id}-bottle`}
                            size="sm"
                            variant="outline"
                            onClick={() => addWine(w, "bottle")}
                            className="border-[#7c2d12] text-[#7c2d12] hover:bg-[#7c2d12] hover:text-white rounded-full h-8 px-3 text-[11px]"
                          >
                            Bottle {money(bottleTotal)}
                          </Button>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </section>
          )}

          {loading && (
            <p className="text-[12px] text-[#5C625E] italic">Loading pairing suggestions…</p>
          )}
        </div>

        {/* iter377 — scroll affordances (right gutter, above the CTA, never over text) */}
        <div className="pointer-events-none absolute right-3 bottom-28 z-30 flex flex-col gap-2">
          {showBackToTop && (
            <button
              type="button"
              data-testid="menu-detail-back-to-top"
              onClick={() => scrollEl?.scrollTo({ top: 0, behavior: "smooth" })}
              className="pointer-events-auto w-9 h-9 rounded-full bg-white/95 hover:bg-white shadow-md border border-[#E8E5DF] text-[#3A3F3B] grid place-items-center transition"
              aria-label="Back to top"
            >
              <ArrowUp className="w-4 h-4" strokeWidth={2} />
            </button>
          )}
          {showScrollDown && (
            <button
              type="button"
              data-testid="menu-detail-scroll-down"
              onClick={() => scrollEl?.scrollBy({ top: 320, behavior: "smooth" })}
              className="pointer-events-auto w-9 h-9 rounded-full bg-[#C05746]/95 hover:bg-[#C05746] shadow-md text-white grid place-items-center animate-bounce transition"
              aria-label="Scroll for more"
            >
              <ChevronDown className="w-5 h-5" strokeWidth={2} />
            </button>
          )}
        </div>

        {/* Sticky primary CTA — auto-calculated price includes selected add-ons */}
        <div className="border-t border-[#E8E5DF] px-10 py-6 bg-white shrink-0">
          <Button
            data-testid="menu-detail-add-primary"
            onClick={() => onAddPrimary?.({
              ...item,
              price: finalPrice,
              selected_addons: Array.from(pickedAddons).map((idx) => item.addons[idx]).filter(Boolean),
              selected_group_options: collectSelectedGroupOptions(),
            })}
            className="w-full h-12 bg-[#C05746] hover:bg-[#A64A3C] text-white rounded-full text-sm font-medium"
          >
            <Plus className="w-4 h-4 mr-1.5" strokeWidth={2} />
            Add {item.name} — {money(finalPrice)}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function Macro({ label, value, unit }) {
  return (
    <div className="rounded-xl bg-[#F3F0EA] p-2.5">
      <p className="text-[18px] font-medium text-[#121614] tabular-nums leading-none">{value}</p>
      <p className="text-[9px] uppercase tracking-wider text-[#5C625E] mt-1">{label} {unit && <span className="opacity-70">{unit}</span>}</p>
    </div>
  );
}
